import { useState, useRef, useEffect } from "react";
import "./SubscriptionDonation.scss";
import logo from "../../assets/logo.png";
import { RAZORPAY_CONFIG } from "../config/razorpayConfig";

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const SHORT_MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

function ordinal(d) {
  if (d >= 11 && d <= 13) return d + "th";
  const s = ["th", "st", "nd", "rd"];
  return d + (s[d % 10] || "th");
}

function startOfDay(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

function formatDate(date) {
  return `${date.getDate()} ${SHORT_MONTHS[date.getMonth()]} ${date.getFullYear()}`;
}

function CalendarDropdown({ value, onChange, disabled, minDate, maxDate }) {
  const [open, setOpen] = useState(false);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0 });
  const [viewMonth, setViewMonth] = useState(value.getMonth());
  const [viewYear, setViewYear] = useState(value.getFullYear());
  const wrapperRef = useRef(null);

  // Compute fixed position — opens downward normally, flips upward if near bottom
  const DROPDOWN_HEIGHT = 320; // approx calendar height in px
  const openDropdown = () => {
    if (disabled) return;
    if (!open && wrapperRef.current) {
      const rect = wrapperRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const openUpward = spaceBelow < DROPDOWN_HEIGHT && spaceAbove > spaceBelow;
      const availableHeight = openUpward
        ? Math.min(spaceAbove - 8, DROPDOWN_HEIGHT)
        : Math.min(spaceBelow - 8, DROPDOWN_HEIGHT);
      setDropdownPos({
        top: openUpward ? undefined : rect.bottom,
        bottom: openUpward ? window.innerHeight - rect.top : undefined,
        left: rect.left,
        width: rect.width,
        maxHeight: availableHeight,
      });
    }
    setOpen(o => !o);
  };

  // Close on outside click — covers mouse AND touch
  useEffect(() => {
    function handleOutside(e) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    if (open) {
      document.addEventListener("mousedown", handleOutside);
      document.addEventListener("touchstart", handleOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleOutside);
      document.removeEventListener("touchstart", handleOutside);
    };
  }, [open]);

  const canGoPrev =
    viewYear > minDate.getFullYear() ||
    (viewYear === minDate.getFullYear() && viewMonth > minDate.getMonth());

  const canGoNext =
    viewYear < maxDate.getFullYear() ||
    (viewYear === maxDate.getFullYear() && viewMonth < maxDate.getMonth());

  const prevMonth = (e) => {
    e.stopPropagation();
    if (!canGoPrev) return;
    if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y - 1); }
    else setViewMonth(m => m - 1);
  };

  const nextMonth = (e) => {
    e.stopPropagation();
    if (!canGoNext) return;
    if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y + 1); }
    else setViewMonth(m => m + 1);
  };

  const firstDayOfWeek = new Date(viewYear, viewMonth, 1).getDay();
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();

  const cells = [];
  for (let i = 0; i < firstDayOfWeek; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const isSelected = (d) =>
    d === value.getDate() && viewMonth === value.getMonth() && viewYear === value.getFullYear();

  const isDisabled = (d) => {
    const date = startOfDay(new Date(viewYear, viewMonth, d));
    return date < minDate || date > maxDate;
  };

  const handleDayClick = (d) => {
    onChange(startOfDay(new Date(viewYear, viewMonth, d)));
    setOpen(false);
  };

  return (
    <div className="cal-dropdown-wrapper" ref={wrapperRef}>
      {/* Input trigger */}
      <div
        className={`cal-input ${open ? "cal-input--open" : ""} ${disabled ? "cal-input--disabled" : ""}`}
        onClick={openDropdown}
        role="button"
        tabIndex={disabled ? -1 : 0}
        onKeyDown={(e) => e.key === "Enter" && openDropdown()}
      >
        <span className="cal-input__icon">📅</span>
        <span className="cal-input__value">{formatDate(value)}</span>
        <span className="cal-input__arrow">{open ? "▲" : "▼"}</span>
      </div>

      {/* Dropdown calendar — fixed position so it escapes overflow:auto parents */}
      {open && (
        <div
          className="cal-dropdown"
          style={{
            top: dropdownPos.top,
            bottom: dropdownPos.bottom,
            left: dropdownPos.left,
            width: dropdownPos.width,
            maxHeight: dropdownPos.maxHeight,
          }}
        >
          <div className="cal-header">
            <button type="button" className="cal-nav" onClick={prevMonth} disabled={!canGoPrev}>‹</button>
            <span className="cal-month-label">{MONTHS[viewMonth]} {viewYear}</span>
            <button type="button" className="cal-nav" onClick={nextMonth} disabled={!canGoNext}>›</button>
          </div>
          <div className="cal-grid">
            {WEEKDAYS.map(w => (
              <div key={w} className="cal-weekday">{w}</div>
            ))}
            {cells.map((d, i) => {
              if (!d) return <div key={`empty-${i}`} />;
              const off = isDisabled(d);
              const sel = isSelected(d);
              return (
                <button
                  key={d}
                  type="button"
                  disabled={off}
                  onClick={() => handleDayClick(d)}
                  className={[
                    "cal-day",
                    sel ? "cal-day--selected" : "",
                    off ? "cal-day--disabled" : "",
                  ].join(" ").trim()}
                >
                  {d}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function SubscriptionDonation(props) {
  const [selectedAmount, setSelectedAmount] = useState(null);
  const [customAmount, setCustomAmount] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);

  const minDate = startOfDay(new Date(Date.now() + 86400000));
  const maxDate = (() => {
    const d = new Date(minDate);
    d.setMonth(d.getMonth() + 1);
    return startOfDay(d);
  })();

  const [selectedDate, setSelectedDate] = useState(minDate);

  const presetAmounts = [100, 250, 500, 1000, 2500];
  const MIN_DONATION_AMOUNT = 100;

  const closeSubscriptionModal = () => props.closeSubscriptionModal(true);

  const handlePresetAmountClick = (amount) => {
    setSelectedAmount(amount);
    setCustomAmount("");
  };

  const handleCustomAmountChange = (e) => {
    const value = e.target.value;
    if (value === "" || /^\d+$/.test(value)) {
      setCustomAmount(value);
      setSelectedAmount(null);
    }
  };

  const getDonationAmount = () => {
    if (selectedAmount) return selectedAmount;
    if (customAmount) return parseFloat(customAmount);
    return 0;
  };

  const initiateSubscription = async () => {
    const donationAmount = getDonationAmount();

    if (!donationAmount || donationAmount < MIN_DONATION_AMOUNT) {
      alert(`Please select or enter a minimum amount of ₹${MIN_DONATION_AMOUNT}`);
      return;
    }

    setIsProcessing(true);

    try {
      const response = await fetch("/api/create-subscription", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          amount: donationAmount,
          startDate: Math.floor(selectedDate.getTime() / 1000),
        }),
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Failed to create subscription");

      if (data.subscription && data.subscription.id) {
        const options = {
          key: RAZORPAY_CONFIG.keyId,
          subscription_id: data.subscription.id,
          name: RAZORPAY_CONFIG.name,
          description: `Monthly donation of ₹${donationAmount} to ISC Foundation`,
          handler: function (response) {
            console.log("Subscription payment successful:", response);
            alert(
              `Mandate set up! ₹${donationAmount} will be auto-debited on the ${ordinal(selectedDate.getDate())} of every month.`
            );
            closeSubscriptionModal();
          },
          prefill: { name: "", email: "", contact: "" },
          notes: { donation_type: "recurring", amount_inr: donationAmount },
          theme: { color: RAZORPAY_CONFIG.theme.color },
          modal: { ondismiss: () => setIsProcessing(false) },
        };

        const rzp = new window.Razorpay(options);
        rzp.on("payment.failed", function (response) {
          console.error("Subscription payment failed:", response.error);
          alert("Payment failed. Please try again.");
          setIsProcessing(false);
        });
        rzp.open();
      } else {
        throw new Error("Invalid subscription response");
      }
    } catch (error) {
      console.error("Subscription creation error:", error);
      alert(`Error: ${error.message}. Please try again.`);
    } finally {
      setIsProcessing(false);
    }
  };

  const chargeDay = ordinal(selectedDate.getDate());

  return (
    <div className="subscription-modal monthly-only">
      <div className="subscription-modal__header">
        <img src={logo} alt="ISC Foundation logo" loading="lazy" />
        <p>ISC Foundation</p>
      </div>

      <h2>Monthly Recurring Donation<span>!</span></h2>
      <p className="subscription-modal__subtitle">
        Support us every month with your contribution
      </p>

      <div className="subscription-form">
        {/* Preset Amount Buttons */}
        <div className="form-group">
          <label>Select Amount (₹)</label>
          <div className="preset-amounts">
            {presetAmounts.map((amount) => (
              <button
                key={amount}
                className={`preset-amount-btn ${selectedAmount === amount ? "active" : ""}`}
                onClick={() => handlePresetAmountClick(amount)}
                disabled={isProcessing}
              >
                ₹{amount}
              </button>
            ))}
          </div>
        </div>

        {/* Custom Amount Input */}
        <div className="form-group">
          <label htmlFor="custom-amount">Or Enter Custom Amount (₹)</label>
          <input
            type="text"
            id="custom-amount"
            className="amount-input"
            value={customAmount}
            onChange={handleCustomAmountChange}
            placeholder={`Min ₹${MIN_DONATION_AMOUNT}`}
            disabled={isProcessing}
          />
        </div>

        {/* Date Picker */}
        <div className="form-group">
          <label>Choose First Charge Date</label>
          <CalendarDropdown
            value={selectedDate}
            onChange={setSelectedDate}
            disabled={isProcessing}
            minDate={minDate}
            maxDate={maxDate}
          />
          <p className="cal-mandate-note">
            Your autopay mandate will be set for the <strong>{chargeDay}</strong> — ₹{getDonationAmount() || "—"} will be auto-debited on this date every month.
          </p>
        </div>

        {/* Preview */}
        {getDonationAmount() >= MIN_DONATION_AMOUNT && (
          <div className="donation-preview">
            <p className="preview-label">Donation Summary</p>
            <div className="preview-details">
              <div className="preview-row">
                <span>Monthly Amount:</span>
                <strong>₹{getDonationAmount()}</strong>
              </div>
              <div className="preview-row">
                <span>First Charge:</span>
                <strong>{formatDate(selectedDate)}</strong>
              </div>
              <div className="preview-row">
                <span>Then Every:</span>
                <strong>{chargeDay} of each month</strong>
              </div>
            </div>
          </div>
        )}

        {/* Subscribe Button */}
        <button
          className="subscribe-btn"
          onClick={initiateSubscription}
          disabled={isProcessing || getDonationAmount() < MIN_DONATION_AMOUNT}
        >
          {isProcessing ? "Processing..." : "Set Up Monthly Donation"}
        </button>

        {/* Close Button */}
        <button
          className="close-btn"
          onClick={closeSubscriptionModal}
          disabled={isProcessing}
        >
          Close
        </button>
      </div>
    </div>
  );
}

export default SubscriptionDonation;
